import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// Mapping of listing_type values to display labels
const LISTING_TYPE_LABELS: Record<string, { label: string; isOffering: boolean }> = {
  // Helicopter offerings (green)
  'sale': { label: 'Helicopters For Sale', isOffering: true },
  'wet_lease': { label: 'Helicopters For Wet Lease', isOffering: true },
  'dry_lease': { label: 'Helicopters For Dry Lease', isOffering: true },
  
  // Helicopter requests (amber)
  'buy_sale': { label: 'Helicopter Purchase Requests', isOffering: false },
  'buy_wet_lease': { label: 'Helicopter Wet Lease Requests', isOffering: false },
  'buy_dry_lease': { label: 'Helicopter Dry Lease Requests', isOffering: false },
  
  // Engine offerings (green)
  'engine_sell': { label: 'Engines For Sale', isOffering: true },
  'engine_rent': { label: 'Engines For Rent', isOffering: true },
  
  // Engine requests (amber)
  'engine_buy': { label: 'Engine Purchase Requests', isOffering: false },
  'engine_rent_request': { label: 'Engine Rent Requests', isOffering: false },
  
  // Parts offerings (green)
  'parts_sell_aircraft': { label: 'Aircraft Parts For Sale', isOffering: true },
  'parts_sell_engine': { label: 'Engine Parts For Sale', isOffering: true },
  'parts_sell_general': { label: 'General Parts For Sale', isOffering: true },
  'parts_rent': { label: 'Parts & Tools For Rent', isOffering: true },
  
  // Parts requests (amber)
  'parts_buy_aircraft': { label: 'Aircraft Parts Wanted', isOffering: false },
  'parts_buy_engine': { label: 'Engine Parts Wanted', isOffering: false },
  'parts_buy_general': { label: 'General Parts Wanted', isOffering: false },
  'parts_rent_request': { label: 'Parts & Tools Rent Requests', isOffering: false },
  
  // Jobs offerings (companies posting)
  'job_post': { label: 'Job Postings', isOffering: true },
  'job_contract': { label: 'Contract Opportunities', isOffering: true },
  
  // Jobs requests (professionals seeking)
  'job_profile': { label: 'Professional Profiles', isOffering: false },
  'job_seeking': { label: 'Candidates Seeking Work', isOffering: false },
  
  // Services offerings (providers)
  'service_offer': { label: 'Service Providers', isOffering: true },
  'capability_listing': { label: 'Capability Listings', isOffering: true },
  
  // Services requests (clients)
  'service_request': { label: 'Service Requests', isOffering: false },
  'rfq_request': { label: 'Request for Quotes', isOffering: false },
};

// For helicopter listings that use is_buy_request boolean instead of listing_type
const HELICOPTER_OFFERING_LABEL = { label: 'Helicopters For Sale', isOffering: true };
const HELICOPTER_REQUEST_LABEL = { label: 'Helicopter Purchase Requests', isOffering: false };

interface CategoryDividerProps {
  listingType: string;
  count: number;
  assetType: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
  isBuyRequest?: boolean; // For helicopter listings that use boolean flag
  className?: string;
}

export const CategoryDivider = ({ 
  listingType, 
  count, 
  assetType,
  isBuyRequest,
  className 
}: CategoryDividerProps) => {
  // Get label and color info
  let labelInfo = LISTING_TYPE_LABELS[listingType];
  
  // Handle helicopter listings that may not have listing_type set
  if (!labelInfo && assetType === 'helicopter') {
    labelInfo = isBuyRequest ? HELICOPTER_REQUEST_LABEL : HELICOPTER_OFFERING_LABEL;
  }
  
  // Fallback for unknown types
  if (!labelInfo) {
    labelInfo = { label: listingType || 'Other Listings', isOffering: true };
  }
  
  const isOffering = labelInfo.isOffering;
  
  return (
    <div className={cn(
      "col-span-full w-full flex items-center gap-3 py-3",
      className
    )}>
      <div className={cn(
        "h-[2px] flex-1 bg-gradient-to-r",
        isOffering 
          ? "from-border/30 via-emerald-500 to-border/30" 
          : "from-border/30 via-amber-500 to-border/30"
      )} />
      <Badge 
        className={cn(
          "text-xs font-medium px-3 py-1 border shadow-sm whitespace-nowrap",
          isOffering 
            ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800"
            : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800"
        )}
      >
        {count} {labelInfo.label}
      </Badge>
      <div className={cn(
        "h-[2px] flex-1 bg-gradient-to-r",
        isOffering 
          ? "from-border/30 via-emerald-500 to-border/30" 
          : "from-border/30 via-amber-500 to-border/30"
      )} />
    </div>
  );
};

// Helper to get display label for a listing type
export const getListingTypeLabel = (listingType: string, assetType: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services', isBuyRequest?: boolean): string => {
  let labelInfo = LISTING_TYPE_LABELS[listingType];
  
  if (!labelInfo && assetType === 'helicopter') {
    labelInfo = isBuyRequest ? HELICOPTER_REQUEST_LABEL : HELICOPTER_OFFERING_LABEL;
  }
  
  return labelInfo?.label || listingType || 'Other';
};

// Helper to check if a listing type is an offering (vs request)
export const isOfferingType = (listingType: string, assetType: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services', isBuyRequest?: boolean): boolean => {
  let labelInfo = LISTING_TYPE_LABELS[listingType];
  
  if (!labelInfo && assetType === 'helicopter') {
    return !isBuyRequest;
  }
  
  return labelInfo?.isOffering ?? true;
};

// Get sort order for listing types (offerings first, then requests)
export const getListingTypeSortOrder = (listingType: string, assetType: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services'): number => {
  const offeringOrder: Record<string, number> = {
    // Helicopter offerings
    'sale': 1,
    'wet_lease': 2,
    'dry_lease': 3,
    // Helicopter requests
    'buy_sale': 10,
    'buy_wet_lease': 11,
    'buy_dry_lease': 12,
    
    // Engine offerings
    'engine_sell': 1,
    'engine_rent': 2,
    // Engine requests
    'engine_buy': 10,
    'engine_rent_request': 11,
    
    // Parts offerings
    'parts_sell_aircraft': 1,
    'parts_sell_engine': 2,
    'parts_sell_general': 3,
    'parts_rent': 4,
    // Parts requests
    'parts_buy_aircraft': 10,
    'parts_buy_engine': 11,
    'parts_buy_general': 12,
    'parts_rent_request': 13,
    
    // Jobs offerings (company posting jobs)
    'job_post': 1,
    'job_contract': 2,
    // Jobs requests (professionals seeking)
    'job_profile': 10,
    'job_seeking': 11,
    
    // Services offerings
    'service_offer': 1,
    'capability_listing': 2,
    // Services requests
    'service_request': 10,
    'rfq_request': 11,
  };
  
  return offeringOrder[listingType] ?? 99;
};
